import {
  InfiniteData,
  MutationFunction,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { communityQueryKeys } from '../constants';
import { toast } from 'react-hot-toast';
import { createDummyCommunityPost } from '../utils/createDummyCommunityPost';
import {
  AddPostMutationVariables,
  Post,
  useAddPostMutation,
} from '../../../../generated/graphql';
import { upload } from '@client/components/ajax';
import { pickObject } from '@client/new/helpers/general';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { containsChannelMention } from '../utils/channelMention';
import { trackEvent } from '@services/tracking';

type CommunityPostPayload = {
  text: string;
  media?: File[];
};

const uploadMedia = async (media: File[], influencerId: number) => {
  const promises = media.map(async (file) => {
    const isVideo = file.type.startsWith('video');
    return upload(file, influencerId, { skipCompression: isVideo });
  });
  return Promise.all(promises);
};
const updater =
  (post: Post) =>
  (previousData?: InfiniteData<Post[]>): InfiniteData<Post[]> => {
    if (previousData?.pages && Array.isArray(previousData?.pages)) {
      const [firstPage, ...restPages] = previousData.pages;
      return {
        ...previousData,
        pages: [[post, ...firstPage], ...restPages],
      };
    }
    return {
      ...previousData,
      pages: [[post]],
    } as InfiniteData<Post[]>;
  };

export const useAddCommunityPost = (
  communityId: number,
  ownerName: string,
  ownerProfilePicture: string,
  onAddSuccess: () => void,
  onAddError: () => void,
) => {
  const influencerId = useInfluencerId();
  const queryClient = useQueryClient();

  const [addPostMutation] = useAddPostMutation({ ignoreResults: true });
  const addPostOnTop = (post: Post) => {
    queryClient.setQueryData<InfiniteData<Post[]>>(
      communityQueryKeys.posts(+communityId),
      updater(post),
    );
  };
  const addPost = async (
    communityId: number,
    postData: CommunityPostPayload,
  ) => {
    const variables: AddPostMutationVariables = {
      communityId: +communityId,
      text: postData.text,
    };

    if (postData.media && influencerId) {
      const media = await uploadMedia(postData.media, influencerId);
      variables.media = media.map((m) =>
        pickObject(m, [
          'audio',
          'cdn01',
          'height',
          'image',
          'mimeType',
          'source',
          'video',
          'width',
        ]),
      );
    }
    const response = await addPostMutation({ variables });
    return response.data?.addPost as Post;
  };

  const mutate: MutationFunction<Post, CommunityPostPayload> = (p) =>
    addPost(communityId, p);

  return useMutation(mutate, {
    onMutate: async (newPostData) => {
      await queryClient.cancelQueries({
        queryKey: communityQueryKeys.posts(+communityId),
      });

      const optimisticPost = createDummyCommunityPost({
        text: newPostData.text,
        username: ownerName,
        profilePicture: ownerProfilePicture,
        media: newPostData.media,
      });

      addPostOnTop(optimisticPost);
    },
    onSuccess: (_, variables) => {
      toast.success('Post created successfully!');

      if (containsChannelMention(variables.text)) {
        trackEvent({
          type: 'Broadcast message sent',
        });
      }

      onAddSuccess();
    },
    onSettled: async () => {
      await queryClient.refetchQueries(communityQueryKeys.posts(+communityId));
    },
    onError: (_) => {
      toast.error('There was an error, try again');
      onAddError();
    },
  });
};
